'use client';

import { useRef, useState } from 'react';
import { DocumentTypeCode, EntityTypeCode } from '@/apiClient';
import { usePrivate } from '@/components/private/PrivateContext';
import { EulaContent } from '@/components/TermsAndConditionsDialog/EulaContent';
import {
  UploadImageModal,
  ACCEPTED_FILE_EXTENSIONS,
} from '@/components/UploadImageModal';
import { slate } from '@/lib/colours';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LoadingButton,
  TextField,
  Typography,
} from '@/primitives';
import { DocumentAvatar } from '@/components/avatars/DocumentAvatar';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';

export interface WelcomeDialogProps {
  loading?: boolean;
  onClickBack: () => void;
  onSubmit: (preferredName: string) => void;
  open: boolean;
}

export function WelcomeDialog({
  loading,
  onClickBack,
  onSubmit,
  open,
}: WelcomeDialogProps) {
  const { userProfile } = usePrivate();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preferredName, setPreferredName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [avatarKey, setAvatarKey] = useState(0);

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setUploadModalOpen(true);
    }
    e.target.value = '';
  };

  const canSubmit =
    preferredName.trim().length > 0 && termsAccepted && !loading;

  const handleSubmit = () => {
    if (canSubmit) {
      onSubmit(preferredName.trim());
    }
  };

  return (
    <>
      <Dialog
        disablePortal
        open={open}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: '520px',
          },
        }}
      >
        <DialogTitle component="div">
          <Typography variant="headlinesmall" sx={{ pb: 1 }}>
            Welcome to BreedLOGIC
          </Typography>
          <Typography variant="bodymedium" color={slate[500]}>
            Let&apos;s get your profile set up
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          {/* Profile photo */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
              pt: 1,
            }}
          >
            <DocumentAvatar
              key={avatarKey}
              documentTypeCode={DocumentTypeCode.Avatar}
              documentEntityId={userProfile?.userProfileEntityId}
              sx={{
                width: 96,
                height: 96,
                border: `2px solid ${slate[200]}`,
                bgcolor: 'action.hover',
                color: 'text.secondary',
              }}
            >
              <PersonOutlinedIcon sx={{ fontSize: 48 }} />
            </DocumentAvatar>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_FILE_EXTENSIONS.join(',')}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              aria-hidden
            />
            <Button variant="outlined" size="small" onClick={openFileSelector}>
              Upload profile photo
            </Button>
          </Box>

          {/* Preferred name */}
          <Box>
            <Typography variant="titlesmall" sx={{ mb: 1 }}>
              What do you want us to call you?
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your preferred name"
              value={preferredName}
              onChange={(e) => setPreferredName(e.target.value)}
              inputProps={{ maxLength: 50 }}
              size="small"
            />
          </Box>

          {/* T&C checkbox */}
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                />
              }
              label={
                <Typography variant="bodysmall">
                  I agree to the{' '}
                  <Typography
                    component="span"
                    variant="bodysmall"
                    sx={{
                      color: 'primary.main',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      setShowTerms(true);
                    }}
                  >
                    Terms &amp; Conditions
                  </Typography>
                </Typography>
              }
            />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClickBack}>Back</Button>
          <LoadingButton
            variant="contained"
            loading={loading}
            loadingText="Submitting..."
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            Submit
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* T&C viewer dialog */}
      <Dialog
        open={showTerms}
        onClose={() => setShowTerms(false)}
        PaperProps={{
          sx: {
            width: '90vw',
            maxWidth: '1000px',
            height: '80vh',
          },
        }}
      >
        <DialogTitle component="div">
          <Typography variant="headlinesmall">
            Terms &amp; Conditions
          </Typography>
        </DialogTitle>
        <DialogContent>
          <EulaContent />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setShowTerms(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo upload modal */}
      <UploadImageModal
        cropSize={{ width: 200, height: 200 }}
        description="Upload a profile photo. You can crop and zoom to fit."
        documentType={DocumentTypeCode.Avatar}
        entityId={userProfile?.userProfileEntityId}
        entityType={EntityTypeCode.UserProfile}
        initialFile={selectedFile}
        onClose={() => {
          setUploadModalOpen(false);
          setSelectedFile(null);
        }}
        onUploaded={() => {
          setUploadModalOpen(false);
          setSelectedFile(null);
          setAvatarKey((k) => k + 1);
        }}
        open={uploadModalOpen}
        title="Upload Profile Photo"
      />
    </>
  );
}
